/**
 * Contact/ticket → template-variable mapping, plus the single `AUTO_FILL_VARIABLES`
 * classifier the manifest derivation, the SQL backfill, and the strict resolver all
 * agree on. Moved out of `apps/hub/lib/utils/crm/template-variables-mappers.ts` so
 * every app shares one implementation (EMAIL_TEMPLATES_CANONICAL_SYSTEM); Hub keeps
 * its codegen `Pick<Contacts, …>` type and re-exports these functions.
 */
/**
 * Return the first argument that is a non-blank string (ignoring surrounding
 * whitespace), or undefined when none qualify. Used so a stale/empty
 * denormalized field falls through to a linked-relation value.
 */
function firstNonBlank(...values) {
    for (const value of values) {
        if (typeof value === "string" && value.trim() !== "") {
            return value;
        }
    }
    return undefined;
}
/** Stringify a numeric-or-string column, dropping null/undefined. */
function asString(value) {
    if (value === null || value === undefined)
        return undefined;
    return String(value);
}
/**
 * Extract template variables from a text string.
 * Finds all {{variable_name}} patterns.
 */
export function extractTemplateVariables(text) {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
        const variable = match[1].trim();
        if (!variables.includes(variable)) {
            variables.push(variable);
        }
    }
    return variables;
}
/** Map ticket data to template variables. */
export function mapTicketToTemplateVariables(ticket) {
    return {
        ticket_number: firstNonBlank(ticket.formatted_ticket_number) ?? asString(ticket.ticket_number),
        ticket_title: ticket.title ?? undefined,
        ticket_status: ticket.status ?? undefined,
        ticket_priority: ticket.priority ?? undefined,
        ticket_category: ticket.category ?? undefined,
        actual_hours: asString(ticket.actual_hours),
    };
}
/** Map contact/client data to template variables. */
export function mapContactToTemplateVariables(contact) {
    // Split name into first and last name if available
    const nameParts = contact.name?.split(" ") || [];
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return {
        // Contact information
        first_name: firstName,
        last_name: lastName,
        name: contact.name ?? undefined,
        email: contact.email ?? undefined,
        phone: contact.phone ?? undefined,
        title: contact.title ?? undefined,
        // Company information - access via companies relation if needed.
        // Prefer the denormalized contacts.company_name, but fall back to the
        // linked company's name (contact.companies.name) when it is blank/stale so
        // {{company_name}} still fills for contacts that have a company linked.
        company_name: firstNonBlank(contact.company_name, contact.companies?.name),
        industry: contact.companies?.industry ?? undefined,
        website: contact.companies?.website ?? undefined,
        // Address information
        address: contact.address ?? undefined,
        city: contact.city ?? undefined,
        state: contact.state ?? undefined,
        zip_code: contact.zip_code ?? undefined,
        country: contact.country ?? undefined,
        // Business information - access via companies relation
        contract_type: contact.companies?.contract_type ?? undefined,
        hourly_rate: asString(contact.companies?.hourly_rate),
        monthly_rate: asString(contact.companies?.monthly_rate),
        payment_terms: contact.companies?.payment_terms ?? undefined,
        // Default sender name (can be customized)
        sender_name: "West Tennessee Consulting",
    };
}
/**
 * Variables that are auto-filled from client/ticket data (or a built-in default)
 * and can still be manually overridden. This is the single classifier the manifest
 * derivation, the SQL backfill, and the strict resolver all agree on — a token in
 * this set is `source: "auto"`, everything else is `manual` and must be supplied
 * explicitly at send time. Keep it in lockstep with what {@link mapContactToTemplateVariables}
 * / {@link mapTicketToTemplateVariables} actually produce a value for.
 */
export const AUTO_FILL_VARIABLES = new Set([
    "first_name",
    "last_name",
    "name",
    "email",
    "phone",
    "title",
    "company_name",
    "industry",
    "website",
    "address",
    "city",
    "state",
    "zip_code",
    "country",
    "contract_type",
    "hourly_rate",
    "monthly_rate",
    "payment_terms",
    // Built-in default supplied by mapContactToTemplateVariables (never blank).
    "sender_name",
    "ticket_number",
    "ticket_title",
    "ticket_status",
    "ticket_priority",
    "ticket_category",
    "actual_hours",
]);
/** Check if a variable is typically auto-filled from client/ticket data. */
export function isAutoFillVariable(varName) {
    return AUTO_FILL_VARIABLES.has(varName);
}
